import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ProjectDTO, ProjectHistory, StepDTO, TimelineEntry } from '@pace/contracts';
import { chainOf } from '@pace/domain';
import { viewOf } from '@pace/contracts';
import { addDays } from '@pace/domain';
import type { Translate } from '@pace/i18n';
import { Button, Field, Icon, PartThumb, Sheet, type IconName } from '@pace/ui';
import type { Lookups } from '../data/bootstrap';
import { useI18n } from '../i18n';
import { api } from '../lib/api';
import { uploadPhoto } from '../lib/photos';
import { fmtDateTime, fmtDay } from '../lib/time';
import { useStepAccess, useStepActions } from './actions';
import { NotifyButton } from './NotifyButton';
import { PhotoButton } from './PhotoButton';
import { Slip, StepChip } from './status';
import { useToast } from './Toasts';

export const toolName = (p: ProjectDTO, s: Pick<StepDTO, 'toolId'>, fallback: string) =>
  s.toolId ? (p.tools.find((t) => t.id === s.toolId)?.name ?? '') : fallback;

/** "Done on…": one tap on Today / Yesterday / 2 or 3 days ago saves; or pick a date. */
export function DoneSheet({
  project,
  step,
  lk,
  onClose,
}: {
  project: ProjectDTO;
  step: StepDTO;
  lk: Lookups;
  onClose: () => void;
}) {
  const { t, lang } = useI18n();
  const actions = useStepActions();
  const today = lk.today();
  const [note, setNote] = useState('');
  const [other, setOther] = useState(step.actual ?? today);
  const save = (date: string) => {
    if (actions.done(project, step, date, note.trim() || null)) onClose();
  };
  const quick: Array<[string, string]> = [
    [today, t('common.today')],
    [addDays(today, -1), t('common.yesterday')],
    [addDays(today, -2), t('common.daysAgo', { n: 2 })],
    [addDays(today, -3), t('common.daysAgo', { n: 3 })],
  ];
  return (
    <Sheet
      title={t('work.whenDone')}
      subtitle={`${lk.stageName(step)} · ${toolName(project, step, project.partName)}`}
      media={<PartThumb name={project.partName} photoUrl={project.photoUrl} size={44} />}
      onClose={onClose}
      closeLabel={t('common.close')}
    >
      <div className="datechips">
        {quick.map(([d, label]) => (
          <button
            key={d}
            type="button"
            className="datechip"
            onClick={() => save(d)}
            aria-pressed={step.actual === d}
          >
            <b>{label}</b>
            <span>{fmtDay(d, lang)}</span>
          </button>
        ))}
      </div>
      <form
        className="form-row"
        style={{ alignItems: 'end' }}
        onSubmit={(e) => {
          e.preventDefault();
          save(other);
        }}
      >
        <Field label={t('work.pickDate')}>
          <input
            className="input"
            type="date"
            value={other}
            max={today}
            onChange={(e) => setOther(e.target.value)}
            required
          />
        </Field>
        <Button type="submit" variant="primary" icon="check">
          {t('common.save')}
        </Button>
      </form>
      <Field label={t('work.noteOptional')}>
        <input
          className="input"
          value={note}
          maxLength={500}
          placeholder={t('work.notePlaceholder')}
          onChange={(e) => setNote(e.target.value)}
        />
      </Field>
    </Sheet>
  );
}

/** Hold → one tap on a reason chip saves (2 taps total). */
export function HoldSheet({
  project,
  step,
  lk,
  onClose,
}: {
  project: ProjectDTO;
  step: StepDTO;
  lk: Lookups;
  onClose: () => void;
}) {
  const { t, lang } = useI18n();
  const actions = useStepActions();
  const [note, setNote] = useState('');
  return (
    <Sheet
      title={t('work.holdTitle')}
      subtitle={`${lk.stageName(step)} · ${toolName(project, step, project.partName)}`}
      media={<PartThumb name={project.partName} photoUrl={project.photoUrl} size={44} />}
      onClose={onClose}
      closeLabel={t('common.close')}
    >
      <Field label={t('work.noteOptional')}>
        <input
          className="input"
          value={note}
          maxLength={500}
          placeholder={t('work.notePlaceholder')}
          onChange={(e) => setNote(e.target.value)}
        />
      </Field>
      <div className="reasons">
        {lk.reasons.map((r) => (
          <button
            key={r.id}
            type="button"
            className="reason"
            onClick={() => {
              actions.hold(project, step, r.id, note.trim() || null);
              onClose();
            }}
          >
            <span className="reason__icon">
              <Icon name={r.icon as IconName} />
            </span>
            {lang === 'hi' ? r.labelHi : r.labelEn}
          </button>
        ))}
      </div>
      <p className="muted" style={{ textAlign: 'center', fontSize: 13 }}>
        {t('work.oneTapSaves')}
      </p>
    </Sheet>
  );
}

export function eventText(e: TimelineEntry, t: Translate, lk: Lookups, lang: 'en' | 'hi'): string {
  switch (e.type) {
    case 'done':
      return t('timeline.done', { date: fmtDay(e.date, lang, true) });
    case 'hold':
      return t('timeline.hold', { reason: lk.reasonLabel(e.reasonId) });
    case 'note':
      return t('timeline.note', { note: e.note ?? '' });
    case 'notify':
      return e.note ? `${t('timeline.notify')}: ${e.note}` : t('timeline.notify');
    default: {
      const key = `timeline.${e.type}` as Parameters<Translate>[0];
      const text = t(key, { name: '' });
      return text === key ? e.type : text.replace(/:\s*$/, '');
    }
  }
}

export function useHistory(projectId: string) {
  return useQuery({
    queryKey: ['history', projectId],
    queryFn: () => api<ProjectHistory>(`/projects/${projectId}/history`),
    staleTime: 5_000,
  });
}

export function usePhotoUpload(project: ProjectDTO, step: StepDTO) {
  const qc = useQueryClient();
  const toast = useToast();
  const { t } = useI18n();
  return useMutation({
    mutationFn: (f: File) => uploadPhoto(`/steps/${step.id}/photos`, f, { max: 1600 }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['history', project.id] });
      toast({ message: t('work.photoAdded') });
    },
    onError: () => toast({ message: t('work.photoNeedsNet'), tone: 'error' }),
  });
}

/** Tap any step → its dates, who owns it, its photos and history, and the actions you may take. */
export function StepSheet({
  project,
  step,
  lk,
  onClose,
  extra,
}: {
  project: ProjectDTO;
  step: StepDTO;
  lk: Lookups;
  onClose: () => void;
  /** Planner tools shown under the actions (plan date, N/A …). */
  extra?: React.ReactNode;
}) {
  const { t, lang } = useI18n();
  const actions = useStepActions();
  const access = useStepAccess()(project, step);
  const [sub, setSub] = useState<'done' | 'hold' | null>(null);
  const [notifyNote, setNotifyNote] = useState('');
  const hist = useHistory(project.id);
  const photo = usePhotoUpload(project, step);
  const view = viewOf(project, lk.today(), lk.cal);
  const v = chainOf(view, step.id);
  const sv = v?.chain[v.index];
  const dept = lk.deptOf(step);
  const owner = lk.ownerOf(dept);
  const tool = toolName(project, step, t('project.milestones'));

  if (sub === 'done') return <DoneSheet project={project} step={step} lk={lk} onClose={() => setSub(null)} />;
  if (sub === 'hold') return <HoldSheet project={project} step={step} lk={lk} onClose={() => setSub(null)} />;

  const canAct = access.access !== 'none';
  const photos = (hist.data?.photos ?? []).filter((p) => p.stepId === step.id);
  const events = (hist.data?.entries ?? []).filter((e) => e.stepId === step.id);
  const row = (k: string, val: React.ReactNode) => (
    <div className="kv">
      <span>{k}</span>
      <b>{val}</b>
    </div>
  );
  return (
    <Sheet
      wide
      side
      title={`${lk.stageName(step)} · ${tool}`}
      subtitle={`${project.partName} · ${project.code}`}
      media={<PartThumb name={project.partName} photoUrl={project.photoUrl} size={48} />}
      onClose={onClose}
      closeLabel={t('common.close')}
    >
      {sv && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <StepChip status={sv.status} />
          <Slip days={sv.slip} long />
        </div>
      )}
      <div className="kvs">
        {row(t('project.baseline'), fmtDay(step.baseline ?? step.planned, lang, true))}
        {step.baseline && step.planned && step.planned !== step.baseline
          ? row(t('project.plan'), fmtDay(step.planned, lang, true))
          : null}
        {step.state === 'done'
          ? row(t('project.actual'), fmtDay(step.actual, lang, true))
          : sv?.forecast
            ? row(t('project.forecast'), fmtDay(sv.forecast, lang, true))
            : null}
        {row(t('project.duration'), t('common.workingDays', { n: step.days }))}
        {row(t('project.owner'), `${lk.deptName(dept)}${owner ? ` · ${owner.name}` : ''}`)}
        {step.state === 'hold' ? row(t('status.HOLD'), lk.reasonLabel(step.holdReasonId)) : null}
        {step.note ? row(t('common.note'), step.note) : null}
      </div>
      <div className="notifyline">
        <input
          className="input"
          value={notifyNote}
          maxLength={300}
          placeholder={t('notify.notePlaceholder')}
          aria-label={t('notify.note')}
          onChange={(e) => setNotifyNote(e.target.value)}
        />
        <NotifyButton
          projectId={project.id}
          stepId={step.id}
          who={owner?.name ?? lk.deptName(dept)}
          message={notifyNote}
          onSent={() => setNotifyNote('')}
        />
      </div>
      {canAct && (
        <div className="form-actions">
          {access.locked ? (
            <span className="lockline">
              <Icon name="lock" />
              {t('work.locked', { name: lk.planner()?.name ?? t('roles.planner') })}
            </span>
          ) : step.state === 'done' ? (
            <>
              <Button icon="calendar" onClick={() => setSub('done')}>
                {t('work.changeDate')}
              </Button>
              <Button variant="ghost" onClick={() => actions.reopen(project, step)}>
                {t('work.notDone')}
              </Button>
            </>
          ) : step.state === 'skipped' ? null : (
            <>
              <Button
                variant="ok"
                icon="check"
                onClick={() => actions.done(project, step, lk.today()) && onClose()}
              >
                {t('work.doneToday')}
              </Button>
              <Button icon="calendar" onClick={() => setSub('done')}>
                {t('work.doneOn')}
              </Button>
              {step.state === 'hold' ? (
                <Button icon="play" onClick={() => actions.resume(project, step)}>
                  {t('work.resume')}
                </Button>
              ) : (
                <Button variant="danger" icon="stop" onClick={() => setSub('hold')}>
                  {t('work.hold')}
                </Button>
              )}
            </>
          )}
          {step.state !== 'skipped' && (
            <PhotoButton
              label={t('common.addPhoto')}
              disabled={photo.isPending}
              onFile={(f) => photo.mutate(f)}
            />
          )}
        </div>
      )}
      {extra}
      {photos.length > 0 && (
        <>
          <p className="eyebrow">{t('common.photos')}</p>
          <div className="photos">
            {photos.map((ph) => (
              <a key={ph.id} href={ph.url} target="_blank" rel="noreferrer" className="photo">
                <img src={ph.thumbUrl} alt={ph.caption || lk.stageName(step)} loading="lazy" />
                <span>{fmtDateTime(ph.at, lk.tz, lang)}</span>
              </a>
            ))}
          </div>
        </>
      )}
      <p className="eyebrow">{t('project.history')}</p>
      <div className="timeline">
        {events.map((e) => (
          <div key={e.id} className={`tl${e.undone ? ' tl--undone' : ''}`}>
            <span className="tl__time">{fmtDateTime(e.at, lk.tz, lang)}</span>
            {eventText(e, t, lk, lang)}
            <span className="muted"> · {lk.userName(e.userId)}</span>
            {e.undone ? <span className="muted"> ({t('timeline.undone')})</span> : null}
          </div>
        ))}
        {hist.isPending && <div className="tl muted">{t('common.loading')}</div>}
        {!hist.isPending && !events.length && <div className="tl muted">{t('project.historyEmpty')}</div>}
      </div>
    </Sheet>
  );
}
